import { IStrongBoxFilter } from '@nkr/models/strong-box/strong-box-filter';
import { Utility } from '@nkr/services/utility';
import { useMemo, useRef, useState } from 'react';
import { Alert, Col, Row } from 'react-bootstrap';
import { LoadingIndicator } from '@nkr/components/loading-indicator';
import { ColDef, GridApi, GridReadyEvent, ICellRendererParams, IHeaderParams, RowClassParams, ValueGetterParams } from 'ag-grid-community';
import { IStrongBoxCenterOrderListing } from '@nkr/models/strong-box/strong-box-order';
import { GridWrapper } from './styles/styles'
import { AgGridReact } from 'ag-grid-react';
import { TLegendEntry } from './legend';
import useStrongBoxUsages from '@nkr/store/strong-box/hooks/use-strong-box-usages';
import SortableHeaderComponent from '../recipients/recipient-shield/components/sortable-header';
import StrongBoxFilter from './strong-box-filter';
import DonorCenterCell from './donor-center-cell';

type IProps = {

}

const StrongBoxUsages = ({ }: IProps) => {
    const defaultFilter: IStrongBoxFilter = {
        startDate: Utility.getFirstOfCurrentMonth(),
        endDate: Utility.getLastOfCurrentMonth(),
        isCenter: true
    }

    const [filter, setFilter] = useState<IStrongBoxFilter>(defaultFilter);
    const { list, loading, error } = useStrongBoxUsages(filter);

    const colDefs: ColDef[] = [
        {
            colId: 'rNum',
            field: 'rNum',
            headerName: 'R#',
            sortable: false,
            headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>R#</SortableHeaderComponent>,
            cellRendererFramework: (params: ICellRendererParams) => <>{params.node.rowIndex + 1}</>,
            width: 20
        },
        {
            colId: 'compositeSwapNumber',
            field: 'compositeSwapNumber',
            headerName: 'Swap #',
            width: 120,
            headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>Swap</SortableHeaderComponent>,
            valueGetter: (props: ValueGetterParams) => {
                const d: IStrongBoxCenterOrderListing = props.data;
                return d.swapInfo?.compositeSwapNumber ? d.swapInfo.compositeSwapNumber : 'N/A';
            },
        },
        {
            colId: 'donorCenter',
            field: 'swapInfo.donorCenter',
            headerName: 'Donor Center',
            width: 120,
            headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>Donor Center</SortableHeaderComponent>,
            cellRendererFramework: (params: ICellRendererParams) => {
                const d: IStrongBoxCenterOrderListing = params.data;
                return <DonorCenterCell donorCenter={d.swapInfo?.donorCenter} remoteDonorCenter={d.swapInfo?.remoteDonorCenter} />;
            },
            valueGetter: (props: ValueGetterParams) => {
                const d: IStrongBoxCenterOrderListing = props.data;
                return d.swapInfo?.donorCenter ? d.swapInfo.donorCenter : 'N/A';
            },
        },
        {
            colId: 'recipientCenter',
            field: 'swapInfo.recipientCenter',
            headerName: 'Recipient Center',
            width: 120,
            headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>Recipient Center</SortableHeaderComponent>,
            valueGetter: (params: ValueGetterParams) => {
                const d: IStrongBoxCenterOrderListing = params.data;
                return d.swapInfo?.recipientCenter ? d.swapInfo.recipientCenter : 'N/A';
            },
        },
        {
            colId: 'kidneyPickupDate',
            field: 'swapInfo.kidneyPickupDate',
            headerName: 'Pickup Date',
            width: 120,
            comparator: Utility.dateComparator,
            headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>Pickup Date</SortableHeaderComponent>,
            cellRendererFramework: (params: ICellRendererParams) => {
                return <>{params.value ? params.value : 'N/A'}</>;
            }
        }
    ];

    const columnDefs = useMemo(() => colDefs, []);

    const apiRef = useRef<GridApi | null>(null);

    const onGridReady = (params: GridReadyEvent) => {
        apiRef.current = params.api;
        params.api.sizeColumnsToFit();
    };

    const expCsv = () => {
        // Call the API method to trigger download
        if (apiRef.current) {
            apiRef.current.exportDataAsCsv({
                columnGroups: false,
                fileName: `StrongBoxUsageHistory.csv`,
                columnKeys: [
                    'compositeSwapNumber',
                    'donorCenter',
                    'recipeintCenter',
                    'kidneyPickupDate'
                ],
            });
        }
    };

    const getRowStyle = (params: RowClassParams) => {
        const d: IStrongBoxCenterOrderListing = params.data;
        if (d?.swapInfo?.isSwapCancelled) {
            return { background: '#D79E9B' };
        }
    }

    const legendEntries: TLegendEntry[] = [
        { text: 'Cancelled Swap', color: '#D79E9B' }
    ];

    return (<>
        <Row>
            <Col sm={12}>
                <StrongBoxFilter
                    filter={filter}
                    defaultFilter={defaultFilter}
                    startDateLabel='Earliest Usage Date'
                    endDateLabel='Latest Usage Date'
                    legendEntries={legendEntries}
                    isCenter={true}
                    onChange={setFilter}
                    onExport={expCsv}
                />
            </Col>
        </Row>

        {loading && <LoadingIndicator type='spin' color='black' loading={true} message={'Loading Usage History ...'} />}

        {error && <Alert bsStyle='danger'>{JSON.stringify(error)}</Alert>}

        {!loading &&
            <Row>
                <Col sm={12}>
                    <GridWrapper className='ag-theme-bootstrap'>
                        <AgGridReact
                            defaultColDef={{
                                headerClass: 'list-header',
                                cellClass: 'list-cell',
                                sortable: true
                            }}
                            columnDefs={columnDefs}
                            rowData={list}
                            headerHeight={30}
                            rowHeight={30}
                            noRowsOverlayComponentFramework={() => <div>No usage history records were found...</div>}
                            suppressRowClickSelection={true}
                            suppressCellSelection={true}
                            enableCellTextSelection={true}
                            onGridReady={onGridReady}
                            onFirstDataRendered={(e) => e.api.sizeColumnsToFit()}
                            onGridSizeChanged={(e) => e.api.sizeColumnsToFit()}
                            onRowDataChanged={(e) => e.api.sizeColumnsToFit()}
                            getRowStyle={getRowStyle}
                        />
                    </GridWrapper>
                </Col>
            </Row>
        }
    </>);
}

export default StrongBoxUsages
